import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  CircleCheck,
  LifeBuoy,
  Mail,
  MapPin,
  Phone,
  Plus,
  Search,
  SearchX,
  Send,
  X,
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import EmptyState from '../../components/site/EmptyState';
import SEO from '../../components/SEO';
import { seoConfig } from '../../configs/seoConfig';
import { ENQUIRY_TYPES, submitEnquiry } from '../../services/enquiryService';
import { getErrorMessage } from '../../utils/apiError';
import {
  EnquirySent,
  Honeypot,
  LIMITS,
  supportHours,
} from '../requestProject/enquiryForm';
import contactInfo, {
  getFullAddress,
  getSupportStatus,
  getWhatsAppUrl,
} from '../../configs/contactInfo';
import { useSiteConfig } from '../../configs/siteConfig';
import { HELP_ARTICLES, HELP_TOPICS } from './helpTopics';
import './ContactPage.css';

// Help Center: searchable answers first, then a way to reach a person.
// Routes: /help and /contact.

const POPULAR_SEARCHES = [
  { label: 'Download link', query: 'download' },
  { label: 'Refunds', query: 'refund' },
  { label: 'GST invoice', query: 'invoice' },
  { label: 'GitHub access', query: 'github' },
];

// Filler words dropped from searches so "how do I get a refund" finds the refund answer.
const STOP_WORDS = new Set(
  'a an and are at be but by can could did do does for from get got has have how i if in is it me my no not of on or our so the this to was we what when where which who why will with you your'.split(
    ' '
  )
);

// "Monday–Friday, 9:00 AM–6:00 PM IST", with the times kept on one line.
// Functions, not constants: the details come from Admin → Settings (see configs/siteConfig.js).
const hoursText = () =>
  `${contactInfo.businessHours.days.replace(' - ', '–')}, ${contactInfo.businessHours.time
    .replace(' - ', '–')
    .replace(/ /g, ' ')}`;

// Email is optional: every email touchpoint stays hidden until a mailbox is configured.
const supportEmail = () => contactInfo.email.support;
const infoEmail = () => contactInfo.email.info;

const segmentsOf = paragraph =>
  typeof paragraph === 'string' ? [paragraph] : paragraph;

const plainText = paragraph =>
  segmentsOf(paragraph)
    .map(seg => (typeof seg === 'string' ? seg : seg.label))
    .join('');

const SEARCH_INDEX = HELP_ARTICLES.map(article => ({
  ...article,
  title: article.q.toLowerCase(),
  keywords: `${article.tags} ${article.topic.label}`.toLowerCase(),
  body: article.a.map(plainText).join(' ').toLowerCase(),
}));

// A word in the question counts most, then the search tags, then the answer text.
const scoreArticle = (article, terms) =>
  terms.reduce((score, term) => {
    if (article.title.includes(term)) return score + 3;
    if (article.keywords.includes(term)) return score + 2;
    if (article.body.includes(term)) return score + 1;
    return score;
  }, 0);

const getTerms = query => {
  const words = query.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const meaningful = words.filter(w => w.length > 1 && !STOP_WORDS.has(w));
  return meaningful.length ? meaningful : words;
};

const searchArticles = terms => {
  if (!terms.length) return SEARCH_INDEX;
  return SEARCH_INDEX.map(article => ({
    article,
    score: scoreArticle(article, terms),
  }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ article }) => article);
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const Highlight = ({ text, terms }) => {
  const usable = terms.filter(term => term.length > 1);
  if (!usable.length) return text;
  const pattern = new RegExp(`(${usable.map(escapeRegExp).join('|')})`, 'gi');
  return text.split(pattern).map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="hc-mark">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

const Paragraph = ({ content, terms }) => (
  <p>
    {segmentsOf(content).map((seg, i) => {
      if (typeof seg === 'string') {
        return <Highlight key={i} text={seg} terms={terms} />;
      }
      return seg.to ? (
        <Link key={i} className="hc-link" to={seg.to}>
          {seg.label}
        </Link>
      ) : (
        <a key={i} className="hc-link" href={seg.href}>
          {seg.label}
        </a>
      );
    })}
  </p>
);

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const useSupportStatus = () => {
  const [status, setStatus] = useState(() => getSupportStatus());
  useEffect(() => {
    const timer = setInterval(() => setStatus(getSupportStatus()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  return status;
};

/* ---------- Header: talk to a person ---------- */

const SupportPanel = () => {
  useSiteConfig();
  const status = useSupportStatus();
  const SUPPORT_EMAIL = supportEmail();
  return (
    <aside className="hc-panel" aria-labelledby="hc-panel-title">
      <p className="hc-status" data-open={status.open}>
        <span className="hc-status__dot" aria-hidden="true" />
        <strong>{status.open ? 'Open now' : 'Closed now'}</strong>
        <span>{status.detail}</span>
      </p>
      <h2 id="hc-panel-title" className="hc-panel__title">
        Talk to a person
      </h2>
      <p className="hc-panel__text">
        WhatsApp or call us {hoursText()}. Outside those hours, you can still
        leave a WhatsApp message.
      </p>
      <div className="hc-panel__actions">
        <a
          className="pk-btn pk-btn--primary pk-btn--sm"
          href={getWhatsAppUrl(
            `Hi ${contactInfo.companyName}, I need help with `
          )}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaWhatsapp aria-hidden="true" />
          WhatsApp us
        </a>
        <a
          className="pk-btn pk-btn--secondary pk-btn--sm"
          href={`tel:${contactInfo.phone.primary.replace(/\s/g, '')}`}
        >
          <Phone aria-hidden="true" />
          Call
        </a>
        {SUPPORT_EMAIL && (
          <a
            className="pk-btn pk-btn--secondary pk-btn--sm"
            href={`mailto:${SUPPORT_EMAIL}`}
          >
            <Mail aria-hidden="true" />
            Email
          </a>
        )}
      </div>
    </aside>
  );
};

/* ---------- Answers ---------- */

const Article = ({ article, terms, open, onToggle }) => (
  <details
    className="hc-q"
    id={article.id}
    open={open}
    onToggle={e => onToggle(article.id, e.currentTarget.open)}
  >
    <summary className="hc-q__sum">
      <span>
        <Highlight text={article.q} terms={terms} />
      </span>
      <Plus aria-hidden="true" />
    </summary>
    <div className="hc-q__body">
      {article.a.map((paragraph, i) => (
        <Paragraph key={i} content={paragraph} terms={terms} />
      ))}
      {article.action && (
        <Link className="hc-q__action" to={article.action.to}>
          {article.action.label}
          <ArrowRight aria-hidden="true" />
        </Link>
      )}
    </div>
  </details>
);

/* ---------- Contact form: saved to the admin inbox (POST /api/enquiries), or continued on WhatsApp ---------- */

const TOPIC_OPTIONS = [
  ...HELP_TOPICS.map(topic => ({ value: topic.id, label: topic.label })),
  { value: 'other', label: 'Something else' },
];

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  topic: '',
  orderId: '',
  message: '',
};
// On-screen order: the first invalid field gets focus.
const FIELD_ORDER = ['name', 'email', 'phone', 'topic', 'orderId', 'message'];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^[0-9+()\-\s]+$/;

// "save" needs an email to reply to; WhatsApp and email continue in another app.
const validate = (form, channel) => {
  const errors = {};
  const name = form.name.trim();
  if (!name) errors.name = 'Enter your name.';
  else if (name.length < 2) errors.name = 'Name must be at least 2 characters.';
  if (channel === 'save') {
    const email = form.email.trim();
    if (!email) errors.email = 'Enter your email so we can reply.';
    else if (!EMAIL_RE.test(email)) errors.email = 'Enter a valid email address.';
    const phone = form.phone.trim();
    if (phone && !PHONE_RE.test(phone)) errors.phone = 'Enter a valid phone number.';
  }
  if (!form.topic) errors.topic = 'Choose what your question is about.';
  if (!form.message.trim()) errors.message = 'Tell us what’s going on.';
  else if (form.message.trim().length < 10)
    errors.message = 'Add a little more detail so we can help.';
  return errors;
};

const topicLabel = form =>
  TOPIC_OPTIONS.find(o => o.value === form.topic)?.label || 'Question';

const subjectFor = form => {
  const order = form.orderId.trim();
  return order
    ? `Question about order #${order} (${topicLabel(form)})`
    : topicLabel(form);
};

const buildMessage = form => {
  const lines = [
    `Hi ${contactInfo.companyName}, I need help with: ${topicLabel(form)}`,
  ];
  if (form.orderId.trim()) lines.push(`Order ID: ${form.orderId.trim()}`);
  lines.push('', form.message.trim(), '', `- ${form.name.trim()}`);
  return lines.join('\n');
};

const ContactForm = ({ draft, prefill }) => {
  useSiteConfig();
  const SUPPORT_EMAIL = supportEmail();
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...prefill }));
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const formRef = useRef(null);
  const messageRef = useRef(null);
  const honeypotRef = useRef(null);

  // /contact?order=... (from an order page) fills in the order and topic.
  useEffect(() => {
    if (prefill) setForm(prev => ({ ...prev, ...prefill }));
  }, [prefill]);

  // A search with no answers hands its query over as the start of a message.
  useEffect(() => {
    if (!draft) return;
    setForm(prev =>
      prev.message.trim() ? prev : { ...prev, message: draft.message }
    );
    const field = messageRef.current;
    if (field) {
      field.focus({ preventScroll: true });
      const end = field.value.length;
      field.setSelectionRange(end, end);
    }
  }, [draft]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
    setSent(null);
    setSaveError('');
  };

  const reset = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setSent(null);
    setSaveError('');
  };

  const send = async channel => {
    if (saving) return;
    const found = validate(form, channel);
    setErrors(found);
    const firstInvalid = FIELD_ORDER.find(field => found[field]);
    if (firstInvalid) {
      formRef.current?.querySelector(`[name="${firstInvalid}"]`)?.focus();
      return;
    }

    if (channel === 'save') {
      setSaving(true);
      setSaveError('');
      try {
        await submitEnquiry({
          type: ENQUIRY_TYPES.CONTACT,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          subject: subjectFor(form),
          message: form.message.trim(),
          orderId: form.orderId.trim() || undefined,
          website: honeypotRef.current?.value || '',
        });
        setSent({ channel: 'saved' });
      } catch (error) {
        setSaveError(
          getErrorMessage(
            error,
            'We couldn’t send your message. Please try again, or use WhatsApp.'
          )
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    const text = buildMessage(form);
    if (channel === 'whatsapp') {
      const url = getWhatsAppUrl(text);
      window.open(url, '_blank', 'noopener,noreferrer');
      setSent({ channel, url });
    } else {
      const url = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
        `Help: ${subjectFor(form)}`
      )}&body=${encodeURIComponent(text)}`;
      window.location.href = url;
      setSent({ channel, url });
    }
  };

  const fieldProps = (name, hint) => ({
    id: `hc-${name}`,
    name,
    value: form[name],
    onChange: handleChange,
    'aria-invalid': errors[name] ? 'true' : undefined,
    'aria-describedby':
      [errors[name] && `hc-${name}-error`, hint && `hc-${name}-hint`]
        .filter(Boolean)
        .join(' ') || undefined,
  });

  const fieldError = name =>
    errors[name] && (
      <p className="pk-error" id={`hc-${name}-error`}>
        {errors[name]}
      </p>
    );

  if (sent?.channel === 'saved') {
    return (
      <div className="hc-form">
        <EnquirySent
          title="Message sent"
          text={`We've received your message. We reply ${supportHours()}.`}
          onReset={reset}
          resetLabel="Send another message"
        />
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className="hc-form"
      noValidate
      onSubmit={e => {
        e.preventDefault();
        send('save');
      }}
      aria-labelledby="hc-form-title"
    >
      <h3 id="hc-form-title" className="hc-form__title">
        Message us
      </h3>
      <p className="hc-form__text">
        We reply {supportHours()}. Prefer WhatsApp? You can continue there
        instead.
      </p>

      <div className="pk-form">
        <div className="pk-form__row">
          <div className="pk-field">
            <label className="pk-label" htmlFor="hc-name">
              Your name
            </label>
            <input
              {...fieldProps('name')}
              className="pk-input"
              type="text"
              autoComplete="name"
              maxLength={LIMITS.name}
            />
            {fieldError('name')}
          </div>

          <div className="pk-field">
            <label className="pk-label" htmlFor="hc-email">
              Email
            </label>
            <input
              {...fieldProps('email')}
              className="pk-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              maxLength={LIMITS.email}
            />
            {fieldError('email')}
          </div>
        </div>

        <div className="pk-form__row">
          <div className="pk-field">
            <label className="pk-label" htmlFor="hc-phone">
              Phone <span className="pk-optional">(optional)</span>
            </label>
            <input
              {...fieldProps('phone')}
              className="pk-input"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              maxLength={LIMITS.phone}
            />
            {fieldError('phone')}
          </div>

          <div className="pk-field">
            <label className="pk-label" htmlFor="hc-topic">
              Topic
            </label>
            <select {...fieldProps('topic')} className="pk-select">
              <option value="" disabled>
                Choose a topic
              </option>
              {TOPIC_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {fieldError('topic')}
          </div>
        </div>

        <div className="pk-field">
          <label className="pk-label" htmlFor="hc-orderId">
            Order ID <span className="pk-optional">(optional)</span>
          </label>
          <input
            {...fieldProps('orderId', true)}
            className="pk-input"
            type="text"
            autoComplete="off"
            spellCheck="false"
            maxLength={100}
          />
          <p className="pk-hint" id="hc-orderId-hint">
            For a purchase, it lets us find your order straight away. It’s
            listed in{' '}
            <Link className="hc-link" to="/profile">
              Your orders
            </Link>
            .
          </p>
        </div>

        <div className="pk-field">
          <label className="pk-label" htmlFor="hc-message">
            What’s going on?
          </label>
          <textarea
            {...fieldProps('message')}
            ref={messageRef}
            className="pk-textarea"
            rows={5}
            maxLength={LIMITS.message}
          />
          {fieldError('message')}
        </div>

        <Honeypot inputRef={honeypotRef} idPrefix="hc" />

        {saveError && (
          <div className="pk-alert" data-tone="danger" role="alert">
            {saveError}
          </div>
        )}

        <div className="hc-form__submit">
          <button
            type="submit"
            className="pk-btn pk-btn--primary"
            aria-busy={saving ? 'true' : undefined}
            disabled={saving}
          >
            <Send aria-hidden="true" />
            {saving ? 'Sending…' : 'Send message'}
          </button>
          <button
            type="button"
            className="pk-btn pk-btn--secondary"
            onClick={() => send('whatsapp')}
          >
            <FaWhatsapp aria-hidden="true" />
            Continue on WhatsApp
          </button>
          {SUPPORT_EMAIL && (
            <button
              type="button"
              className="hc-alt"
              onClick={() => send('email')}
            >
              Send by email instead
            </button>
          )}
        </div>

        <div aria-live="polite">
          {sent && (
            <div className="pk-alert" data-tone="success">
              <CircleCheck aria-hidden="true" />
              <p>
                <strong>
                  {sent.channel === 'whatsapp'
                    ? 'WhatsApp opened in a new tab.'
                    : 'Your email app should be opening.'}
                </strong>{' '}
                Press send there to reach us. Didn’t open?{' '}
                <a
                  className="hc-link"
                  href={sent.url}
                  target={sent.channel === 'whatsapp' ? '_blank' : undefined}
                  rel="noopener noreferrer"
                >
                  Try again
                </a>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

const channels = () =>
  [
    {
      icon: FaWhatsapp,
      label: 'WhatsApp',
      value: contactInfo.whatsapp.display,
      href: getWhatsAppUrl(),
      external: true,
    },
    {
      icon: Phone,
      label: 'Phone',
      value: contactInfo.phone.display,
      href: `tel:${contactInfo.phone.primary.replace(/\s/g, '')}`,
      note: hoursText(),
    },
    supportEmail() && {
      icon: Mail,
      label: 'Orders and support',
      value: supportEmail(),
      href: `mailto:${supportEmail()}`,
    },
    infoEmail() && {
      icon: Mail,
      label: 'General questions',
      value: infoEmail(),
      href: `mailto:${infoEmail()}`,
    },
    {
      icon: MapPin,
      label: 'Based in',
      value: getFullAddress(),
    },
  ].filter(Boolean);

const Channels = () => {
  useSiteConfig();
  return (
    <ul className="hc-channels" aria-label="Other ways to reach us">
      {channels().map(({ icon: Icon, label, value, href, note, external }) => (
        <li key={label} className="hc-channel">
          <span className="hc-channel__icon" aria-hidden="true">
            <Icon />
          </span>
          <span className="hc-channel__label">{label}</span>
          <span className="hc-channel__value">
            {href ? (
              <a
                href={href}
                {...(external && {
                  target: '_blank',
                  rel: 'noopener noreferrer',
                })}
              >
                {value}
              </a>
            ) : (
              value
            )}
          </span>
          {note && <span className="hc-channel__note">{note}</span>}
        </li>
      ))}
    </ul>
  );
};

/* ---------- Page ---------- */

const ContactPage = () => {
  useSiteConfig();
  const { hash, search } = useLocation();
  const [query, setQuery] = useState('');
  const [activeTopic, setActiveTopic] = useState('all');
  const [openIds, setOpenIds] = useState(() => new Set());
  const [scrollTarget, setScrollTarget] = useState(null);
  const [draft, setDraft] = useState(null);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const contactRef = useRef(null);

  // /contact?order=... (from "Contact support" on an order) opens the form pre-filled.
  const orderParam = useMemo(
    () => (new URLSearchParams(search).get('order') || '').trim().slice(0, 100),
    [search]
  );
  const prefill = useMemo(
    () =>
      orderParam
        ? {
            orderId: orderParam,
            topic: 'orders',
            message: `Question about order #${orderParam}: `,
          }
        : null,
    [orderParam]
  );

  const terms = useMemo(() => getTerms(query), [query]);
  const results = useMemo(() => searchArticles(terms), [terms]);
  const visible =
    activeTopic === 'all'
      ? results
      : results.filter(article => article.topic.id === activeTopic);
  // Topics keep their usual order; while searching, the topic with the best match comes first.
  const topicOrder = terms.length
    ? [...new Set(visible.map(article => article.topic.id))]
    : HELP_TOPICS.map(topic => topic.id);
  const groups = topicOrder
    .map(topicId => HELP_TOPICS.find(topic => topic.id === topicId))
    .map(topic => ({
      ...topic,
      items: visible.filter(article => article.topic.id === topic.id),
    }))
    .filter(group => group.items.length);
  const countFor = topicId =>
    results.filter(article => article.topic.id === topicId).length;
  const activeLabel = HELP_TOPICS.find(t => t.id === activeTopic)?.label;

  const setOpen = useCallback((id, open) => {
    setOpenIds(prev => {
      if (prev.has(id) === open) return prev;
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Deep links: /help#refund opens that answer, /help#topic-payments jumps to a topic,
  // /contact#contact jumps to the form.
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    if (!id) return;
    const isArticle = HELP_ARTICLES.some(article => article.id === id);
    if (isArticle || id.startsWith('topic-')) {
      setQuery('');
      setActiveTopic('all');
    }
    if (isArticle) setOpen(id, true);
    setScrollTarget({ id });
  }, [hash, setOpen]);

  useEffect(() => {
    if (orderParam) setScrollTarget({ id: 'contact' });
  }, [orderParam]);

  // Scroll once the render that reset the filters has committed.
  useEffect(() => {
    if (!scrollTarget) return;
    document.getElementById(scrollTarget.id)?.scrollIntoView({ block: 'start' });
    setScrollTarget(null);
  }, [scrollTarget]);

  // "/" focuses the search from anywhere on the page.
  useEffect(() => {
    const onKeyDown = e => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      if (el?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el?.tagName))
        return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const runSearch = value => {
    setQuery(value);
    setActiveTopic('all');
  };

  const clearSearch = () => {
    setQuery('');
    searchRef.current?.focus();
  };

  const chooseTopic = topicId => {
    setActiveTopic(topicId);
    const list = resultsRef.current;
    if (list && list.getBoundingClientRect().top < 0) {
      list.scrollIntoView({
        block: 'start',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }
  };

  const askAboutSearch = () => {
    setDraft({
      message: `I couldn’t find an answer for “${query.trim()}”.\n\n`,
    });
    contactRef.current?.scrollIntoView({
      block: 'start',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  const announcement = query.trim()
    ? `${visible.length} ${visible.length === 1 ? 'answer' : 'answers'} for “${query.trim()}”${activeLabel ? ` in ${activeLabel}` : ''}`
    : '';

  return (
    <div className="hc">
      <SEO {...seoConfig.pages.contact} />
      <div className="pk-page hc-top">
        <div className="pk-container">
          <header className="hc-head">
            <div className="hc-head__main">
              <nav aria-label="Breadcrumb">
                <ol className="pk-crumbs">
                  <li>
                    <Link to="/">Home</Link>
                  </li>
                  <li>
                    <span aria-current="page">Help Center</span>
                  </li>
                </ol>
              </nav>
              <h1 className="pk-head__title">How can we help?</h1>
              <p className="hc-sub">
                Answers about orders, payments, refunds and GitHub access. If
                you’re stuck, a real person is one message away.
              </p>

              <div className="hc-search" role="search">
                <label htmlFor="hc-search-input" className="sr-only">
                  Search help
                </label>
                <Search className="hc-search__icon" aria-hidden="true" />
                <input
                  id="hc-search-input"
                  ref={searchRef}
                  className="hc-search__input"
                  type="search"
                  placeholder="Search for an answer"
                  value={query}
                  onChange={e => runSearch(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Escape' && query) {
                      e.preventDefault();
                      setQuery('');
                    }
                  }}
                  autoComplete="off"
                  spellCheck="false"
                  aria-controls="hc-results"
                />
                {query ? (
                  <button
                    type="button"
                    className="hc-search__clear"
                    onClick={clearSearch}
                    aria-label="Clear search"
                  >
                    <X aria-hidden="true" />
                  </button>
                ) : (
                  <kbd className="hc-search__kbd" title="Press / to search">
                    /
                  </kbd>
                )}
              </div>

              <div className="hc-popular">
                <span>Popular:</span>
                {POPULAR_SEARCHES.map(item => (
                  <button
                    key={item.query}
                    type="button"
                    className="hc-popular__btn"
                    aria-pressed={query === item.query}
                    onClick={() =>
                      runSearch(query === item.query ? '' : item.query)
                    }
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <SupportPanel />
          </header>

          <div className="hc-body">
            <nav className="hc-rail" aria-label="Help topics">
              <ul>
                <li>
                  <button
                    type="button"
                    className="hc-rail__btn"
                    aria-pressed={activeTopic === 'all'}
                    onClick={() => chooseTopic('all')}
                  >
                    <LifeBuoy aria-hidden="true" />
                    <span className="hc-rail__label">All topics</span>
                    <span className="hc-rail__count">{results.length}</span>
                  </button>
                </li>
                {HELP_TOPICS.map(({ id, label, icon: Icon }) => {
                  const count = countFor(id);
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        className="hc-rail__btn"
                        aria-pressed={activeTopic === id}
                        data-empty={count === 0 || undefined}
                        onClick={() => chooseTopic(id)}
                      >
                        <Icon aria-hidden="true" />
                        <span className="hc-rail__label">{label}</span>
                        <span className="hc-rail__count">{count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="hc-results" id="hc-results" ref={resultsRef}>
              <p className="sr-only" aria-live="polite">
                {announcement}
              </p>

              {groups.map(group => {
                const Icon = group.icon;
                return (
                  <section
                    key={group.id}
                    className="hc-group"
                    id={`topic-${group.id}`}
                    aria-labelledby={`topic-${group.id}-title`}
                  >
                    <h2 className="hc-group__title" id={`topic-${group.id}-title`}>
                      <span className="hc-group__icon" aria-hidden="true">
                        <Icon />
                      </span>
                      {group.label}
                    </h2>
                    <div className="hc-list">
                      {group.items.map(article => (
                        <Article
                          key={article.id}
                          article={article}
                          terms={terms}
                          open={openIds.has(article.id)}
                          onToggle={setOpen}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}

              {groups.length === 0 &&
                (results.length > 0 ? (
                  <EmptyState
                    icon={<SearchX />}
                    title={`Nothing in ${activeLabel} for “${query.trim()}”`}
                    text={`${results.length} ${results.length === 1 ? 'answer matches' : 'answers match'} in other topics.`}
                    actions={
                      <button
                        type="button"
                        className="pk-btn pk-btn--secondary pk-btn--sm"
                        onClick={() => chooseTopic('all')}
                      >
                        Search all topics
                      </button>
                    }
                  />
                ) : (
                  <EmptyState
                    icon={<SearchX />}
                    title={`No answers for “${query.trim()}”`}
                    text="Try another word, like “payment” or “download”. Or ask us, and we’ll answer it ourselves."
                    actions={
                      <>
                        <button
                          type="button"
                          className="pk-btn pk-btn--primary pk-btn--sm"
                          onClick={askAboutSearch}
                        >
                          Ask us about this
                        </button>
                        <button
                          type="button"
                          className="pk-btn pk-btn--secondary pk-btn--sm"
                          onClick={clearSearch}
                        >
                          Clear search
                        </button>
                      </>
                    }
                  />
                ))}
            </div>
          </div>
        </div>
      </div>

      <section
        className="hc-contact"
        id="contact"
        ref={contactRef}
        aria-labelledby="hc-contact-title"
      >
        <div className="pk-container hc-contact__grid">
          <div className="hc-contact__intro">
            <h2 id="hc-contact-title" className="hc-contact__title">
              Still need help?
            </h2>
            <p className="hc-contact__sub">
              Send us a message and we’ll reply {supportHours()}. For a
              purchase, include the order ID so we can find it straight away.
            </p>
          </div>
          <div className="hc-contact__form">
            <ContactForm draft={draft} prefill={prefill} />
          </div>
          <Channels />
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
